class HeroCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.realSlides = Array.from(this.querySelectorAll('[data-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    this.prevButton = this.querySelector('[data-prev]');
    this.nextButton = this.querySelector('[data-next]');

    if (!this.track || this.realSlides.length < 2) return;

    this.count = this.realSlides.length;
    this.loop = this.hasAttribute('data-loop');
    this.paused = false;
    this.rafId = null;
    this.settleTimer = null;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.loop) {
      this.buildLoop();
    } else {
      this.slides = this.realSlides;
      this.position = 0;
    }

    this.onScroll = this.onScroll.bind(this);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.track.addEventListener('keydown', this.onKeydown.bind(this));

    this.prevButton?.addEventListener('click', () => this.step(-1, true));
    this.nextButton?.addEventListener('click', () => this.step(1, true));
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.goToSlide(Number(dot.dataset.index), true));
    });

    ['pointerenter', 'focusin'].forEach((evt) =>
      this.addEventListener(evt, () => {
        this.paused = true;
      })
    );
    ['pointerleave', 'focusout'].forEach((evt) =>
      this.addEventListener(evt, () => {
        this.paused = false;
      })
    );
    document.addEventListener('visibilitychange', () => {
      this.paused = document.hidden;
    });

    this.onResize = this.onResize.bind(this);
    window.addEventListener('resize', this.onResize, { passive: true });

    this.startAutoplay();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    if (this.rafId) cancelAnimationFrame(this.rafId);
    clearTimeout(this.settleTimer);
    clearTimeout(this.resizeTimer);
    this.stopAutoplay();
  }

  /* A copy of the whole set is added before and after the real slides, so moving
     past the last slide keeps travelling forwards into a copy of the first one.
     Once the scroll settles on a copy we jump back to the matching real slide
     without animating, which is invisible because both look identical. */
  buildLoop() {
    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();

    this.realSlides.forEach((slide) => {
      before.appendChild(this.cloneSlide(slide));
      after.appendChild(this.cloneSlide(slide));
    });

    this.track.insertBefore(before, this.realSlides[0]);
    this.track.appendChild(after);

    this.slides = Array.from(this.track.children);
    this.position = this.count;

    requestAnimationFrame(() => this.jumpTo(this.position));
  }

  cloneSlide(slide) {
    const clone = slide.cloneNode(true);
    clone.removeAttribute('data-slide');
    clone.setAttribute('aria-hidden', 'true');
    clone.classList.remove('is-active');
    clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
    clone.querySelectorAll('img').forEach((img) => {
      img.setAttribute('loading', 'lazy');
      img.removeAttribute('fetchpriority');
    });
    return clone;
  }

  leftFor(index) {
    const target = this.slides[index];
    if (!target) return this.track.scrollLeft;
    return Math.max(0, target.offsetLeft - (this.track.clientWidth - target.clientWidth) / 2);
  }

  /* Direct assignment plus a temporary override, because scrollTo({behavior:'auto'})
     still honours `scroll-behavior: smooth` from the stylesheet. */
  jumpTo(index) {
    const previous = this.track.style.scrollBehavior;
    this.track.style.scrollBehavior = 'auto';
    this.track.scrollLeft = this.leftFor(index);
    this.track.style.scrollBehavior = previous;
    this.position = index;
    this.render();
  }

  slideTo(index) {
    if (this.reducedMotion) {
      this.jumpTo(index);
      return;
    }
    this.track.scrollTo({ left: this.leftFor(index), behavior: 'smooth' });
  }

  onScroll() {
    if (this.rafId) return;
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.updateActive();
    });

    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => this.normalize(), 150);
  }

  onResize() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => this.jumpTo(this.position), 150);
  }

  /* The active slide is the one whose centre sits closest to the centre of the
     viewport. Visibility ratios are ambiguous on desktop, where several slides
     are partly on screen at once. */
  updateActive() {
    const centre = this.track.scrollLeft + this.track.clientWidth / 2;
    let best = this.position;
    let bestDistance = Infinity;

    this.slides.forEach((slide, i) => {
      const slideCentre = slide.offsetLeft + slide.clientWidth / 2;
      const distance = Math.abs(slideCentre - centre);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    });

    if (best === this.position) return;
    this.position = best;
    this.render();
  }

  /* Once movement stops on one of the copies, hop to the equivalent real slide. */
  normalize() {
    if (!this.loop) return;
    if (this.position >= this.count && this.position < this.count * 2) return;

    const real = ((this.position % this.count) + this.count) % this.count;
    this.jumpTo(this.count + real);
  }

  currentSlideIndex() {
    return this.loop ? ((this.position % this.count) + this.count) % this.count : this.position;
  }

  render() {
    const current = this.currentSlideIndex();

    this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === this.position));
    this.dots.forEach((dot, i) => {
      const active = i === current;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  }

  step(direction, userInitiated = false) {
    if (userInitiated) this.restartAutoplay();

    if (this.loop) {
      this.slideTo(this.position + direction);
      return;
    }

    this.slideTo((this.position + direction + this.count) % this.count);
  }

  goToSlide(slideIndex, userInitiated = false) {
    if (userInitiated) this.restartAutoplay();
    this.slideTo(this.loop ? this.count + slideIndex : slideIndex);
  }

  onKeydown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.step(1, true);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.step(-1, true);
    }
  }

  startAutoplay() {
    if (!this.hasAttribute('data-autoplay') || this.reducedMotion) return;
    const speed = Number(this.dataset.autoplaySpeed) || 6000;
    this.timer = setInterval(() => {
      if (!this.paused) this.step(1);
    }, speed);
  }

  stopAutoplay() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }
}

if (!customElements.get('hero-carousel')) {
  customElements.define('hero-carousel', HeroCarousel);
}
